// CORE
// Este é um arquivo core do boilerplate e modificações neste arquivo serão bloqueadas via pré-hook de commit.
// Fazemos este bloqueio para manter a consistência do boilerplate e diminuir as chances de conflitos nos updates.
// Caso possível, procure uma alternativa ao uso deste arquivo.
// Se a alteração for realmente necessária ou for um update do boilerplate,
// realize o commit usando o comando 'git commit --no-verify'.

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';

const SIDEBAR_GRADIENT =
  'linear-gradient(180deg, hsl(270, 60%, 12%) 0%, hsl(270, 55%, 18%) 50%, hsl(270, 50%, 22%) 100%)';

import { useRouter } from 'next/router';
import * as React from 'react';
import { createContext, useContext, useState } from 'react';
import { DrawerContents } from './sidebarContents';

export const drawerWidth = 280;
export const drawerClosedWidth = 88;

const SIDEBAR_STATE_KEY = 'sidebar_state';

const SidebarContext = createContext();

export const SidebarProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(undefined);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), {
    noSsr: true,
  });

  useEffect(() => {
    const localStorageSideBarState = localStorage.getItem(SIDEBAR_STATE_KEY);
    if (
      localStorageSideBarState === 'undefined' ||
      localStorageSideBarState === null
    ) {
      const defaultState = isMobile ? 'closed' : 'open';
      localStorage.setItem(SIDEBAR_STATE_KEY, defaultState);
    }
    setIsOpen(localStorageSideBarState !== 'closed');
    if (isMobile) {
      setIsOpen(false);
    }
  }, []);

  const toggleSidebar = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    //localStorage é meio bugado quando não armazena string
    localStorage.setItem(SIDEBAR_STATE_KEY, newState ? 'open' : 'closed');
  };

  return (
    <SidebarContext.Provider value={{ isOpen, toggleSidebar }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar deve estar dentro de um SidebarProvider');
  }
  return context;
};

export function Sidebar() {
  const { isOpen, toggleSidebar } = useSidebar();

  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(1, 2),
    ...theme.mixins.toolbar,
    position: isOpen ? 'relative' : 'relative',
    top: 0,
    right: 0,
  }));

  if (isOpen == null) return null;

  return (
    <>
      <Drawer
        variant='temporary'
        open={isOpen}
        onClose={toggleSidebar}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            background: SIDEBAR_GRADIENT,
            color: 'neutral.0',
            boxSizing: 'border-box',
            width: isOpen ? drawerWidth : drawerClosedWidth,
            transition: 'width 0.3s',
            overflowX: 'hidden',
            overflow: 'hidden',
            zIndex: 3,
          },
        }}
      >
        <DrawerContents
          isOpen={isOpen}
          toggleSidebar={toggleSidebar}
          DrawerHeader={DrawerHeader}
        />
      </Drawer>
      <Drawer
        variant='permanent'
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            background: SIDEBAR_GRADIENT,
            color: 'neutral.0',
            boxSizing: 'border-box',
            width: isOpen ? drawerWidth : drawerClosedWidth,
            transition: 'width 0.3s',
            overflowX: 'hidden',
            overflow: 'hidden',
            position: 'initial',
            height: '100vh',
            zIndex: 3,
          },
        }}
        open
      >
        <DrawerContents
          isOpen={isOpen}
          toggleSidebar={toggleSidebar}
          DrawerHeader={DrawerHeader}
        />
      </Drawer>
    </>
  );
}

export function SidebarButton({
  name,
  Icon,
  EndIcon,
  path,
  isIndented,
  selectedCallback,
  isInsideAccordionSummary,
  disabled,
}) {
  const router = useRouter();
  const buttonSelected =
    router.pathname.endsWith(path) && !isInsideAccordionSummary;
  const { isOpen } = useSidebar();

  React.useEffect(() => {
    if (buttonSelected && !!selectedCallback && !isInsideAccordionSummary) {
      selectedCallback();
    }
  }, [
    router.pathname,
    buttonSelected,
    selectedCallback,
    isInsideAccordionSummary,
  ]);

  const bg = buttonSelected ? 'primary.darker' : null;

  const startIcon = (
    <Icon sx={{ marginLeft: '18px', marginRight: isOpen ? '18px' : '0' }} />
  );
  const endIcon = EndIcon && isOpen ? <EndIcon /> : undefined;

  const commonContainerStyle = {
    width: '100%',
    color: 'neutral.0',
    justifyContent: 'flex-start',
    py: 0,
    height: '56px',
    minWidth: '56px',
    backgroundColor: bg,
    textWrap: 'nowrap',
  };

  const animatedNameSpan = (
    <span
      style={{
        opacity: isOpen ? 1 : 0,
        transition: 'opacity 0.8s',
      }}
    >
      {isOpen ? name : ''}
    </span>
  );

  if (isInsideAccordionSummary) {
    return (
      <Button
        component='div'
        fullWidth
        size='medium'
        variant='text'
        disabled={disabled}
        sx={{
          ...commonContainerStyle,
          pl: '4px',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: bg || 'transparent',
          },
        }}
        startIcon={startIcon}
        endIcon={endIcon}
      >
        {animatedNameSpan}
      </Button>
    );
  }

  return (
    <Button
      fullWidth
      size='medium'
      variant={'text'}
      href={path}
      disabled={disabled}
      sx={{
        ...commonContainerStyle,
        pl: isIndented ? '56px' : '4px',
      }}
      startIcon={startIcon}
      endIcon={endIcon}
    >
      {animatedNameSpan}
    </Button>
  );
}

export function SectionTitle({ title }) {
  const { isOpen } = useSidebar();

  if (!isOpen) {
    return (
      <Divider
        sx={{
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          marginX: '16px',
          borderColor: 'neutral.0',
          opacity: 0.3,
          '&::before, &::after': {
            borderColor: 'neutral.0',
          },
        }}
      />
    );
  }

  return (
    <Typography
      variant='body-small'
      sx={{
        color: 'neutral.0',
        justifyContent: 'left',
        height: '40px',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        paddingLeft: '16px',
        display: 'flex',
        alignItems: 'center',
        opacity: 0.6,
      }}
    >
      {title}
    </Typography>
  );
}

function SidebarGroupExpandIcon({ expanded, ...props }) {
  return (
    <ExpandMoreIcon
      {...props}
      sx={{
        ...props.sx,
        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.2s ease',
      }}
    />
  );
}

export function SidebarGroup({ name, Icon, children }) {
  const { isOpen } = useSidebar();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const expandGroup = React.useCallback(() => {
    setIsExpanded(true);
  }, []);
  const handleChange = (_event, expanded) => {
    setIsExpanded(expanded);
  };
  const ExpandIcon = React.useCallback(
    (props) => <SidebarGroupExpandIcon {...props} expanded={isExpanded} />,
    [isExpanded],
  );

  return (
    <Accordion
      disableGutters={true}
      expanded={isExpanded}
      onChange={handleChange}
      sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        sx={{
          padding: 0,
          margin: 0,
          '& .MuiAccordionSummary-content': { display: 'block', margin: 0 },
          justifyContent: 'left',
          minHeight: 56,
          '&.Mui-expanded': { minHeight: 56 },
        }}
      >
        <SidebarButton
          name={name}
          Icon={Icon}
          EndIcon={ExpandIcon}
          isInsideAccordionSummary={true}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        {React.Children.map(children, (child) =>
          React.cloneElement(child, {
            isIndented: isOpen,
            selectedCallback: expandGroup,
          }),
        )}
      </AccordionDetails>
    </Accordion>
  );
}
